import React, { createContext, useContext, useState, useEffect } from 'react';
import { initKmfcClient } from '../lib/supabase';

const WEBVONK_URL = 'https://swxfaqoftmwphbparotf.supabase.co';
const WEBVONK_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InN3eGZhcW9mdG13cGhicGFyb3RmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1MjY1OTQsImV4cCI6MjA5NTEwMjU5NH0.IZZAiD-ndE_CvkuY3E44N8Sm7u1EXnsYaYnCda6R-v4';

async function refreshAccessToken(refreshToken) {
  const res = await fetch(`${WEBVONK_URL}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': WEBVONK_ANON_KEY },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) return null;
  return res.json();
}

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const initializeSession = async () => {
      try {
        // Check if we already have user data (don't overwrite existing session)
        const existingUser = localStorage.getItem('kmfc_user');
        
        if (existingUser && mounted) {
          console.log('🔄 Existing user data found, using localStorage session');
          
          try {
            let userData = JSON.parse(existingUser);
            
            // Check if session is not too old (24 hours)
            const loginTime = new Date(userData.login_time);
            const now = new Date();
            const hoursDiff = (now - loginTime) / (1000 * 60 * 60);
            
            const tokenExpired = userData.expires_at && (userData.expires_at * 1000) < Date.now();

            if (tokenExpired && userData.refresh_token) {
              console.log('🔄 Token verlopen, vernieuwen met refresh token...');
              const refreshed = await refreshAccessToken(userData.refresh_token);
              if (refreshed?.access_token) {
                const updated = {
                  ...userData,
                  access_token: refreshed.access_token,
                  refresh_token: refreshed.refresh_token,
                  expires_at: refreshed.expires_at,
                };
                localStorage.setItem('kmfc_user', JSON.stringify(updated));
                userData = updated;
                console.log('✅ Token vernieuwd');
              } else {
                console.log('❌ Refresh mislukt, uitloggen');
                localStorage.removeItem('kmfc_user');
                setUser(null); setSession(null);
                setLoading(false); setIsReady(true);
                return;
              }
            } else if (tokenExpired) {
              localStorage.removeItem('kmfc_user');
              setUser(null); setSession(null);
              setLoading(false); setIsReady(true);
              return;
            }

            if (hoursDiff < 24 * 30) {
              // Valid session found
              setUser(userData);
              setSession({ user: userData }); // Mock session voor compatibiliteit
              console.log('✅ Valid session found:', userData);
              
              // Initialize KMFC client with company slug
              if (userData.company_slug) {
                console.log('🔄 Initializing KMFC client...');
                initKmfcClient(userData.company_slug)
                  .then(() => {
                    console.log(`✅ KMFC client initialized for ${userData.company_slug}`);
                    // Trigger een re-render na initialisatie
                    setTimeout(() => {
                      console.log('🔄 Triggering re-render after KMFC init...');
                      setIsReady(prev => !prev);
                      setTimeout(() => setIsReady(prev => !prev), 100);
                    }, 500);
                  })
                  .catch((kmfcError) => {
                    console.error('❌ Failed to initialize KMFC client:', kmfcError);
                  });
              } else {
                console.log('❌ No company_slug found in user data');
              }
            } else {
              // Session expired
              localStorage.removeItem('kmfc_user');
              setUser(null);
              setSession(null);
            }
          } catch (parseError) {
            console.error('❌ Error parsing stored user data:', parseError);
            localStorage.removeItem('kmfc_user');
            setUser(null);
            setSession(null);
          }
        } else {
          setUser(null);
          setSession(null);
        }
        
        setLoading(false);
        setIsReady(true);
      } catch (error) {
        console.error('SessionProvider: Error in initializeSession:', error);
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
          setIsReady(true);
        }
      }
    };

    initializeSession();

    return () => {
      mounted = false;
    };
  }, []);

  const login = (userData) => {
    setUser(userData);
    setSession({ user: userData });
  };

  const value = {
    session,
    user,
    loading,
    isReady,
    isAuthenticated: !!user,  // Custom auth: check user instead of session
    login,
    logout: () => {
      console.log('🔥 LOGOUT KNOP GEDRUKT - START NUCLEAIRE CLEANUP');
      
      // Reset state EERST voor zekerheid
      setUser(null);
      setSession(null);
      
      // Maak localStorage volledig leeg - ALLES verwijderen
      try {
        // Methode 1: localStorage.clear()
        localStorage.clear();
        console.log('🗑️ localStorage volledig geleegd (methode 1)');
        
        // Methode 2: Alle keys handmatig verwijderen
        const keysToRemove = ['kmfc_user', 'supabase.auth.token', 'supabase.auth.refreshToken'];
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
        console.log('🧹 Specifieke keys ook verwijderd (methode 2)');
        
        // Methode 3: Iteratie over alle keys voor 100% zekerheid
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key) {
            localStorage.removeItem(key);
            console.log(`🗑️ Key verwijderd: ${key}`);
          }
        }
        
        // Methode 4: SessionStorage ook leegmaken
        sessionStorage.clear();
        console.log('🗑️ sessionStorage ook geleegd');
        
        // Methode 5: Cookies verwijderen - verbeterde versie
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          const value = eqPos > -1 ? cookie.substr(eqPos + 1) : "";
          
          if (name.trim() !== "") {
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;domain=" + window.location.hostname + "';";
            console.log(`🍪 Cookie verwijderd: ${name.trim()}`);
          }
        }
        
        console.log('✅ ALLES opgeschoond - 5 methoden gebruikt');
      } catch (error) {
        console.error('❌ Fout bij localStorage clear:', error);
      }
      
      // Kleine delay voor zekerheid
      setTimeout(() => {
        // Stuur naar mijn.webvonk.com
        window.location.href = '/';
      }, 100);
    }
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};
